import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SmartStoreLink } from "@/components/landing/AppStoreBadges";
import { btnLift, eyebrow, focusRing, h2Lg } from "./styles";

const PLANS = [
  { key: "free", features: ["portfolio", "holdings", "health", "analyses", "calendar"], featured: false },
  { key: "premium", features: ["unlimited", "ai", "calendar", "forecasting"], featured: true },
] as const;

const Plans = () => {
  const { t } = useTranslation();

  return (
    <section
      id="plans"
      aria-labelledby="plans-h"
      className="scroll-mt-[72px] bg-white px-[22px] py-[clamp(88px,9vw,128px)]"
    >
      <div className="mx-auto max-w-[880px]">
        <div className="pf-rv text-center">
          <p className={eyebrow}>{t("home.plans.eyebrow")}</p>
          <h2 id="plans-h" className={h2Lg}>
            {t("home.plans.title")}
          </h2>
        </div>

        <div className="pf-rv2 mx-auto mt-[60px] grid max-w-[520px] grid-cols-1 items-stretch gap-5 md:max-w-none md:grid-cols-2">
          {PLANS.map(({ key, features, featured }) => (
            <div
              key={key}
              className={`relative flex flex-col rounded-[28px] px-[26px] py-8 md:px-9 md:py-10 ${
                featured
                  ? "border-2 border-primary bg-white shadow-[0_30px_60px_-36px_hsl(var(--foreground)/0.35)]"
                  : "bg-background"
              }`}
            >
              {featured && (
                <span className="absolute -top-3.5 start-[34px] rounded-full bg-primary px-3 py-[5px] text-xs font-semibold tracking-[0.04em] text-white">
                  {t("home.plans.premium.badge")}
                </span>
              )}
              <h3 className="text-2xl font-semibold tracking-[-0.02em]">{t(`home.plans.${key}.name`)}</h3>
              <p className="mt-[18px] text-[40px] font-bold leading-none tracking-[-0.035em] tabular-nums">
                {t(`home.plans.${key}.price`)}
                {featured && (
                  <span className="ms-1.5 text-[17px] font-medium tracking-normal text-pf-ink2">
                    {t("home.plans.premium.per")}
                  </span>
                )}
              </p>
              <p className="mt-2 text-[15px] text-pf-ink2">{t(`home.plans.${key}.tagline`)}</p>
              <ul className="mt-7 flex list-none flex-col gap-3 border-t border-foreground/10 p-0 pt-6 text-base">
                {features.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check aria-hidden="true" className="mt-px h-5 w-5 flex-none text-primary" strokeWidth={2} />
                    {t(`home.plans.${key}.features.${f}`)}
                  </li>
                ))}
              </ul>
              <span aria-hidden="true" className="block min-h-8 flex-1" />
              <SmartStoreLink
                location={`plans_${key}`}
                utmCampaign="home_clarity"
                className={`flex h-[50px] items-center justify-center rounded-full text-base font-semibold ${btnLift} ${focusRing} ${
                  featured ? "bg-primary text-white" : "border border-foreground text-foreground"
                }`}
              >
                {() => t("home.plans.cta")}
              </SmartStoreLink>
            </div>
          ))}
        </div>

        <p className="mx-auto mt-7 max-w-[620px] text-center text-sm leading-normal text-pf-ink2">
          {t("home.plans.note")}
        </p>
      </div>
    </section>
  );
};

export default Plans;
